// Camera ingest, /footage/ingest (Context.HeliconflowCameraIngest.md A-3), on src/ui (HD-8; DESIGN rev 2.1 §3.2: a
// Workbench page, dark tier, rail sidebar).
//
// The PYXIS 6K records BRAW plus an HD H.264 proxy to a camera-attached USB-C SSD. A PA plugs the drive in, picks its
// folder once, and this page sweeps the NEW proxies into Helicon, each flagged "original is BRAW". No installed software:
// the browser's directory picker (File System Access API, Chrome / Edge).
//
// READ-ONLY on the drive: the page only lists folders and reads files (values() / getFile()). It never writes, moves,
// renames or deletes, and never opens anything but *.mp4 / *.MP4: BRAW is invisible to it.
//
// The API's ingest/check is the truth for what is already in Helicon, never this page's memory, so a re-run after an
// interruption uploads only what is missing. The picked folder handle is kept in the browser's own IndexedDB: the next
// session re-asks permission, not a fresh pick. Nothing about the drive is stored server-side.
//
// HD-8: the files go up through the global Upload sheet (enqueueUpload, pool "ingest", 2 at a time as before) with the
// same multipart endpoints and the same confirm: POST /api/footage/multipart/{start,sign-part,complete,abort} with
// { clientId, replaceStaleFootageIds? }, then POST /api/footage/confirm { footageId, originalExt? }. The preflight (scan,
// POST /api/footage/ingest/check before every batch), the skip rules, the duplicate and name-taken notes, the "re-confirm
// only" retry of a file whose bytes are in, and the warning before closing the tab while files upload are kept. The
// page's own progress bars are gone (the sheet shows them); the batch table keeps each file's state and note.
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, FolderOpen, HardDrive, RefreshCw, RotateCcw, Upload } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import EmptyState from '../ui/EmptyState';
import Icon from '../ui/Icon';
import Select from '../ui/Select';
import Table from '../ui/Table';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import { dismissUpload, enqueueUpload, retryUpload, useUploadDone, useUploads } from '../shell/uploads';

// Owner call 4: the raw-footage upload roles MINUS external users. The API enforces the same set (uniform 404).
const INGEST_ROLES = ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'];
const MAX_PARALLEL = 2;
const LAST_CLIENT_KEY = 'helicon.ingest.clientId';
const CHECK_CHUNK = 5000;
const ENDPOINTS = { start: '/api/footage/multipart/start', signPart: '/api/footage/multipart/sign-part', complete: '/api/footage/multipart/complete', abort: '/api/footage/multipart/abort' };

const isIngestName = name => /\.mp4$/i.test(name) && !name.startsWith('._');

function fmtSize(b) {
  const n = Number(b) || 0;
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}
const errText = err => err?.response?.data?.error || err?.message || 'The upload failed. Retry it.';
const keyOf = f => `${f.name}|${f.size}`;
const plural = (n, w) => `${n} ${n === 1 ? w : `${w}s`}`;

// ─── The browser's own storage for the folder handle (IndexedDB: handles are structured-cloneable) ────────────────────

const IDB_NAME = 'helicon-camera-ingest', IDB_STORE = 'handles', IDB_KEY = 'drive';
function idb(mode, fn) {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(IDB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(IDB_STORE);
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const tx = open.result.transaction(IDB_STORE, mode);
      const r = fn(tx.objectStore(IDB_STORE));
      tx.oncomplete = () => { open.result.close(); resolve(r?.result); };
      tx.onerror = () => { open.result.close(); reject(tx.error); };
    };
  });
}
const loadHandle = () => idb('readonly', s => s.get(IDB_KEY)).catch(() => null);
const saveHandle = h => idb('readwrite', s => s.put(h, IDB_KEY)).catch(() => {});

// Recursive READ-ONLY walk: list entries, read .mp4 metadata. Hidden folders (.Trashes, .Spotlight-V100 …) are skipped.
async function scanDirectory(dir, prefix = '', out = []) {
  for await (const entry of dir.values()) {
    if (entry.kind === 'directory') {
      if (!entry.name.startsWith('.')) await scanDirectory(entry, `${prefix}${entry.name}/`, out);
    } else if (isIngestName(entry.name)) {
      const file = await entry.getFile();
      out.push({ name: entry.name, size: file.size, path: `${prefix}${entry.name}`, handle: entry });
    }
  }
  return out;
}

const STATUS_COPY = {
  exists:      'Already in Helicon',
  in_progress: 'An upload is in progress elsewhere, skipped this run',
  failed:      'An earlier upload failed verification. Resolve it in Raw footage.',
};

export default function CameraIngest() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const uploads = useUploads();
  const canIngest = !user?.isExternal && (user?.roles || []).some(r => INGEST_ROLES.includes(r));
  const supported = typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

  const [handle, setHandle]           = useState(null);
  const [permission, setPermission]   = useState(null);   // 'granted' | 'prompt' | 'denied'
  const [scanning, setScanning]       = useState(false);
  const [scanError, setScanError]     = useState('');
  const [drive, setDrive]             = useState(null);    // { files, results: Map key → check result, scannedAt, dupNames }
  const [clients, setClients]         = useState([]);
  const [clientId, setClientId]       = useState(() => localStorage.getItem(LAST_CLIENT_KEY) || '');
  const [braw, setBraw]               = useState(true);
  const [batch, setBatch]             = useState([]);      // rows { key, name, size, path, state: 'queued'|'skipped'|'failed', uploadId?, error?, note? }
  const [showSkipped, setShowSkipped] = useState(false);
  const fileByKey = useRef(new Map());
  const running   = useRef(false);
  const brawRef   = useRef(braw);
  // Files whose bytes are in but whose confirm call failed (not a verification failure): Retry re-confirms that upload in
  // the queue rather than sending the file again (its pending row would otherwise read as "in progress elsewhere").
  const unconfirmed = useRef(new WeakSet());
  useEffect(() => { brawRef.current = braw; }, [braw]);

  usePageHeader({
    title: 'Ingest from camera drive',
    actions: <Button variant="quiet" icon={ArrowLeft} onClick={() => navigate(canIngest ? '/footage' : '/dashboard')}>{canIngest ? 'Raw footage' : 'Home'}</Button>,
  });

  useEffect(() => {
    if (!canIngest) return;
    api.get('/api/clients').then(r => setClients(r.data.filter(c => c.isActive))).catch(() => {});
  }, [canIngest]);

  // The remembered drive: re-ask permission only (a click is needed for the prompt, so it is a button, not automatic).
  useEffect(() => {
    if (!canIngest || !supported) return;
    loadHandle().then(async h => {
      if (!h) return;
      setHandle(h);
      try { setPermission(await h.queryPermission({ mode: 'read' })); } catch { setPermission('prompt'); }
    });
  }, [canIngest, supported]);

  // This batch's rows joined with the queue: a queued row's state is its upload's state in the sheet.
  // A finished upload may be cleared from the sheet, so its result is kept here; a file that left the sheet unfinished was
  // cancelled or dismissed there.
  const [finished, setFinished] = useState({});   // uploadId → confirm result
  useUploadDone(item => { if (item.meta?.kind === 'ingest') setFinished(m => ({ ...m, [item.id]: item.result || {} })); });
  const byId = new Map(uploads.items.map(i => [i.id, i]));
  const rows = batch.map(r => {
    if (!r.uploadId) return r;
    const u = byId.get(r.uploadId);
    if (!u) return finished[r.uploadId] ? { ...r, state: 'done', note: finished[r.uploadId].note || r.note } : { ...r, state: 'failed', error: 'Cancelled or removed in the upload sheet. Retry it.' };
    if (u.status === 'done') return { ...r, state: 'done', note: u.result?.note || r.note };
    if (u.status === 'failed') return { ...r, state: 'failed', error: u.error };
    return { ...r, state: u.status, progress: u.progress };      // 'queued' | 'uploading'
  });
  const uploading = rows.some(r => r.state === 'queued' || r.state === 'uploading');
  useEffect(() => {
    if (!uploading) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  const check = useCallback(async (files) => {
    const results = new Map();
    for (let i = 0; i < files.length; i += CHECK_CHUNK) {
      const chunk = files.slice(i, i + CHECK_CHUNK);
      const { data } = await api.post('/api/footage/ingest/check', { clientId: clientId || null, files: chunk.map(f => ({ name: f.name, size: f.size })) });
      data.results.forEach(r => results.set(keyOf(r), r));
    }
    return results;
  }, [clientId]);

  // Refresh the picture from the API, the truth, once a run has finished uploading.
  const wasUploading = useRef(false);
  useEffect(() => {
    if (wasUploading.current && !uploading && drive?.files?.length) {
      check(drive.files).then(results => setDrive(d => ({ ...d, results }))).catch(() => { /* keep the old picture */ });
    }
    wasUploading.current = uploading;
  }, [uploading]); // eslint-disable-line react-hooks/exhaustive-deps

  const scan = useCallback(async (dir) => {
    setScanning(true); setScanError('');
    try {
      const found = await scanDirectory(dir);
      // the same file twice on the drive (same name + size, two folders) is one file
      const unique = new Map();
      const dupNames = [];
      for (const f of found) { if (unique.has(keyOf(f))) dupNames.push(f.path); else unique.set(keyOf(f), f); }
      const files = [...unique.values()].sort((a, b) => a.path.localeCompare(b.path));
      fileByKey.current = unique;
      const results = files.length ? await check(files) : new Map();
      setDrive({ files, results, scannedAt: new Date(), dupNames });
    } catch (err) {
      setScanError(err?.name === 'NotAllowedError' ? 'The browser did not allow reading the drive. Reconnect it and try again.' : errText(err));
    } finally {
      setScanning(false);
    }
  }, [check]);

  async function pickFolder() {
    try {
      const dir = await window.showDirectoryPicker({ id: 'helicon-camera-drive', mode: 'read' });
      setHandle(dir); setPermission('granted'); setBatch([]);
      await saveHandle(dir);
      await scan(dir);
    } catch (err) {
      if (err?.name !== 'AbortError') setScanError(errText(err));
    }
  }
  async function reconnect() {
    try {
      const p = await handle.requestPermission({ mode: 'read' });
      setPermission(p);
      if (p === 'granted') await scan(handle);
    } catch (err) { setScanError(errText(err)); }
  }

  function chooseClient(id) {
    setClientId(id);
    if (id) localStorage.setItem(LAST_CLIENT_KEY, id);
  }

  // The confirm step, run by the queue once S3 has the object (Retry in the sheet repeats only this). A verification
  // failure fails the file; a duplicate is stored and says so.
  async function confirmIngest(footageId, file) {
    const body = { footageId };
    if (brawRef.current) body.originalExt = 'braw';
    let data;
    try {
      ({ data } = await api.post('/api/footage/confirm', body));
    } catch (err) {
      unconfirmed.current.add(file);
      throw new Error(`Uploaded, but Helicon did not confirm it: ${errText(err)}`);
    }
    unconfirmed.current.delete(file);
    if (data?.verified === false) throw new Error(data?.verification?.error || 'Verification failed: the uploaded file does not match the drive.');
    return { id: footageId, note: data?.isPossibleDuplicate ? `Stored as a possible duplicate${data.duplicateOfName ? ` of ${data.duplicateOfName}` : ''}` : undefined };
  }

  // Upload `keys`, always re-checked against the API first, so only files that are still missing go up. `fresh` = a new
  // batch (the Upload button); Retry keeps the batch. A file whose bytes are in but whose confirm failed (not a
  // verification failure) is re-confirmed in the queue instead of sent again.
  async function upload(keys, { fresh = false } = {}) {
    if (running.current || !clientId) return;
    running.current = true;
    try {
      const prior = new Map(batch.map(r => [r.key, r]));
      const reconfirm = keys.filter(k => { const r = rows.find(x => x.key === k); return r?.state === 'failed' && r.uploadId && byId.has(r.uploadId) && r.file && unconfirmed.current.has(r.file); });
      for (const k of reconfirm) retryUpload(prior.get(k).uploadId);
      keys = keys.filter(k => !reconfirm.includes(k));
      if (!keys.length) return;
      for (const k of keys) { const r = prior.get(k); if (r?.uploadId) dismissUpload(r.uploadId); }
      const files = keys.map(k => fileByKey.current.get(k)).filter(Boolean);
      let results;
      try { results = await check(files); } catch (err) { setScanError(errText(err)); return; }
      const todo = files.filter(f => ['new', 'stale'].includes(results.get(keyOf(f))?.status));
      const next = [];
      for (const f of files) {
        const r = results.get(keyOf(f));
        const go = todo.includes(f);
        const row = { key: keyOf(f), name: f.name, size: f.size, path: f.path, state: go ? 'queued' : 'skipped', error: go ? undefined : (STATUS_COPY[r?.status] || 'Skipped'),
          note: go && r?.nameTaken ? 'A different file already uses this name. It will be stored with a -1 suffix.' : undefined };
        if (go) {
          let file;
          try {
            file = await f.handle.getFile();
            if (file.size !== f.size) throw new Error('The file changed on the drive since the scan. Rescan.');
          } catch (err) { next.push({ ...row, state: 'failed', error: errText(err) }); continue; }
          const startBody = { clientId };
          if (r.status === 'stale') startBody.replaceStaleFootageIds = r.staleFootageIds;
          row.file = file;
          row.uploadId = enqueueUpload({ file, endpoints: ENDPOINTS, startBody, idFieldName: 'footageId', confirm: confirmIngest, limit: MAX_PARALLEL, meta: { kind: 'ingest', clientId } });
        }
        next.push(row);
      }
      setBatch(list => (fresh ? next : [...list.filter(i => !keys.includes(i.key)), ...next]));
    } finally {
      running.current = false;
    }
  }

  if (!canIngest) {
    return (
      <div className="ui ui-page" data-testid="ingest-not-found">
        <EmptyState message="Camera ingest is not available on this account. It is for the team's editors and producers." />
      </div>
    );
  }

  const results = drive?.results || new Map();
  const statusOf = f => results.get(keyOf(f))?.status;
  const newFiles = (drive?.files || []).filter(f => ['new', 'stale'].includes(statusOf(f)));
  const skipped  = (drive?.files || []).filter(f => !['new', 'stale'].includes(statusOf(f)));
  const newBytes = newFiles.reduce((s, f) => s + f.size, 0);
  const counted  = rows.filter(i => i.state !== 'skipped');
  const done     = counted.filter(i => i.state === 'done').length;
  const failed   = counted.filter(i => i.state === 'failed');
  const stateCell = i => (
    i.state === 'done' ? <Badge tone="ok">Uploaded</Badge>
      : i.state === 'failed' ? <Badge tone="danger">Failed</Badge>
      : i.state === 'uploading' ? <span className="ui-tc">{Math.round((i.progress || 0) * 100)}%</span>
      : <span className="ui-meta">Waiting</span>
  );

  return (
    <div className="ui ui-page ui-page--narrow" data-testid="camera-ingest">
      {!supported ? (
        <section className="ui-region ui-region--pad ui-stack ui-stack--tight" data-testid="ingest-unsupported">
          <h2 className="ui-heading">This page needs Google Chrome or Microsoft Edge on a computer</h2>
          <p className="ui-p ui-text-2">It reads the camera drive through the browser's folder picker, which this browser does not offer. Open Helicon in Chrome or Edge and come back to this page.</p>
        </section>
      ) : (<>
        <p className="ui-p ui-text-2">
          Plug in the camera SSD, pick its folder, and the new MP4 proxies go up to Helicon. This page never changes, moves or deletes anything on the drive, and ignores everything that is not an .mp4 (the BRAW originals stay where they are).
        </p>

        {/* 1: the drive */}
        <section className="ui-section" aria-labelledby="ingest-drive">
          <h2 className="ui-section__title" id="ingest-drive">Camera drive</h2>
          <div className="ui-cluster">
            {handle && <span className="ui-cluster" data-testid="ingest-drive-name"><Icon icon={HardDrive} /><span className="ui-dense">{handle.name}</span></span>}
            {handle && permission !== 'granted' && (
              <Button variant="primary" icon={RotateCcw} onClick={reconnect} data-testid="ingest-reconnect">Reconnect {handle.name}</Button>
            )}
            {handle && permission === 'granted' && (
              <Button variant="secondary" icon={RefreshCw} disabled={scanning || uploading} onClick={() => scan(handle)} data-testid="ingest-rescan">Rescan</Button>
            )}
            <Button variant={handle ? 'quiet' : 'primary'} icon={FolderOpen} disabled={scanning || uploading} onClick={pickFolder} data-testid="ingest-pick">
              {handle ? 'Choose a different folder' : 'Choose the camera drive folder'}
            </Button>
          </div>
          {handle && permission !== 'granted' && <p className="ui-p ui-meta">The browser asks again each session before this page may read the drive.</p>}
          {scanning && <p className="ui-p ui-dense" role="status" data-testid="ingest-scanning">Scanning the drive</p>}
          {scanError && <p className="ui-p ui-field__error" role="alert" data-testid="ingest-error">{clean(scanError)}</p>}
          {drive && !scanning && (
            <div className="ui-stack ui-stack--tight" data-testid="ingest-summary">
              <p className="ui-p ui-dense">
                <strong data-testid="ingest-new-count">{plural(newFiles.length, 'new file')}</strong>
                {newFiles.length > 0 && <>, <span data-testid="ingest-new-size" className="ui-tc">{fmtSize(newBytes)}</span></>}
                <span className="ui-text-2">. {drive.files.length} MP4 on the drive, {skipped.length} skipped.</span>
              </p>
              {drive.dupNames.length > 0 && <p className="ui-p ui-meta">{drive.dupNames.length === 1 ? '1 file appears' : `${drive.dupNames.length} files appear`} twice on the drive and is counted once.</p>}
            </div>
          )}
        </section>

        {/* 2: where it goes */}
        {drive && (
          <section className="ui-section" aria-labelledby="ingest-target">
            <h2 className="ui-section__title" id="ingest-target">Client and format</h2>
            <div className="ui-toolbar ui-toolbar--end">
              <Select label="Client" value={clientId} onChange={e => chooseClient(e.target.value)} disabled={uploading} data-testid="ingest-client"
                placeholder="Choose a client" options={clients.map(c => ({ value: c.id, label: clean(c.name) }))} />
              <Checkbox label="Original is BRAW" checked={braw} onChange={e => setBraw(e.target.checked)} disabled={uploading} data-testid="ingest-braw" />
            </div>
            <p className="ui-p ui-meta">With Original is BRAW on, each upload is marked as a proxy, so the cut-list download points Premiere at the .braw of the same name. Do not rename the files.</p>
            <div className="ui-cluster">
              <Button variant="primary" icon={Upload} disabled={!clientId || uploading || !newFiles.length} onClick={() => upload(newFiles.map(keyOf), { fresh: true })} data-testid="ingest-upload">
                {newFiles.length ? `Upload ${plural(newFiles.length, 'new file')} (${fmtSize(newBytes)})` : 'Nothing new to upload'}
              </Button>
              {!clientId && newFiles.length > 0 && <span className="ui-meta">Choose a client first.</span>}
            </div>
          </section>
        )}

        {/* 3: this batch (progress per file is in the Upload sheet) */}
        {counted.length > 0 && (
          <section className="ui-section" aria-labelledby="ingest-batch">
            <div className="ui-section__head ui-section__head--center">
              <h2 className="ui-section__title" id="ingest-batch" data-testid="ingest-progress">{done} of {counted.length} uploaded</h2>
              {failed.length > 0 && !uploading && (
                <span className="ui-section__actions">
                  <Button variant="secondary" icon={RotateCcw} onClick={() => upload(failed.map(i => i.key))} data-testid="ingest-retry-all">Retry {failed.length} failed</Button>
                </span>
              )}
            </div>
            <Table caption="This batch" rowKey={i => i.key} maxHeight={420}
              columns={[
                { key: 'name', label: 'File', render: i => <span data-testid="ingest-item" data-state={i.state === 'uploading' ? 'uploading' : i.state} data-name={i.name} title={i.path}>{i.name}</span> },
                { key: 'size', label: 'Size', width: 96, render: i => <span className="ui-tc">{fmtSize(i.size)}</span> },
                { key: 'state', label: 'State', width: 120, render: stateCell },
                { key: 'note', label: 'Note', width: 260, render: i => <span className={i.error ? 'ui-text-danger' : 'ui-text-2'} title={i.error || i.note}>{clean(i.error || i.note || '')}</span> },
                { key: 'act', label: 'Retry', width: 96, render: i => (i.state === 'failed' && !uploading ? <Button variant="quiet" onClick={() => upload([i.key])} data-testid="ingest-retry">Retry</Button> : null) },
              ]}
              rows={counted} />
          </section>
        )}

        {/* already in Helicon / skipped, and why */}
        {drive && skipped.length > 0 && (
          <section className="ui-section" aria-label="Skipped files">
            <span>
              <button type="button" className="ui-disclosure ui-dense ui-text-2" aria-expanded={showSkipped} onClick={() => setShowSkipped(v => !v)} data-testid="ingest-skipped-toggle">
                <Icon icon={ChevronDown} />Skipped ({skipped.length})
              </button>
            </span>
            {showSkipped && (
              <Table caption="Skipped files and why" rowKey={f => keyOf(f)} maxHeight={420}
                columns={[
                  { key: 'name', label: 'File', render: f => { const r = results.get(keyOf(f)); return <span data-testid="ingest-skipped" data-name={f.name} data-status={r?.status} title={f.path}>{f.name}</span>; } },
                  { key: 'why', label: 'Why', width: 360, render: f => {
                    const r = results.get(keyOf(f));
                    const where = r?.match ? `: ${clean(r.match.where)}${r.match.archived ? ', archived' : ''}${r.match.fileName !== f.name ? ` (as ${r.match.fileName})` : ''}` : '';
                    const text = `${STATUS_COPY[r?.status] || r?.status || 'Skipped'}${where}`;
                    return <span className="ui-text-2" title={text}>{text}</span>;
                  } },
                ]}
                rows={skipped} />
            )}
          </section>
        )}
      </>)}
    </div>
  );
}
